import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, CheckCircle, CreditCard, Loader } from 'lucide-react';
import {
  collection, doc, query, where, addDoc, updateDoc,
  onSnapshot, Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { notifyClientBudgetApproved } from '../../services/notificationService';
import './BudgetModal.css';

const STATUS_LABELS = {
  draft: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' },
  revision: { label: 'Revision', color: '#d97706', bg: '#fef3c7' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7' },
  paid: { label: 'Paid', color: '#1d4ed8', bg: '#dbeafe' },
};

const formatDate = (timestamp) => {
  if (!timestamp) return null;
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BudgetModal = ({ project, milestones, currentUser, isAdmin, onClose }) => {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [hasVat, setHasVat] = useState(project?.hasVat || false);
  const vatRate = 21;

  const canEdit = (isAdmin || currentUser?.permissions?.canEditBudgets) && 
    !['approved', 'paid'].includes(budget?.status || 'draft');

  useEffect(() => {
    if (!project?.id) return;
    const q = isAdmin
      ? query(collection(db, 'budgets'), where('projectId', '==', project.id))
      : query(collection(db, 'budgets'), where('projectId', '==', project.id), where('clientId', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const data = { id: snap.docs[0].id, ...snap.docs[0].data() };
        setBudget(data);
        setItems(data.items || []);
        setHasVat(data.hasVat ?? project?.hasVat ?? false);
      } else {
        setBudget(null);
        setItems([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Budget load error:', error);
      setLoading(false);
    });
    return () => unsub();
  }, [project?.id]);

  const groups = () => {
    const map = {};
    milestones.forEach(m => {
      map[m.id] = { milestoneId: m.id, milestoneName: m.title, milestoneDate: m.createdAt, items: [] };
    });
    map['__extras__'] = { milestoneId: '__extras__', milestoneName: 'Extras', milestoneDate: null, items: [] };
    items.forEach(item => {
      const key = item.milestoneId || '__extras__';
      if (!map[key]) map[key] = { milestoneId: key, milestoneName: item.milestoneName || 'Extras', milestoneDate: null, items: [] };
      map[key].items.push(item);
    });
    return Object.values(map);
  };

  const lineTotal = (item) => (parseFloat(item.qty) || 1) * (parseFloat(item.price) || 0);
  const subtotal = items.reduce((sum, i) => sum + lineTotal(i), 0);
  const vatAmount = hasVat ? subtotal * (vatRate / 100) : 0;
  const total = subtotal + vatAmount;

  const addItem = (milestoneId, milestoneName) => {
    setItems(prev => [...prev, {
      id: Date.now().toString(),
      milestoneId: milestoneId === '__extras__' ? null : milestoneId,
      milestoneName: milestoneId === '__extras__' ? null : milestoneName,
      qty: '',
      concept: '',
      price: '',
    }]);
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const handleSave = async (newStatus) => {
    setSaving(true);
    try {
      const data = {
        projectId: project.id,
        projectName: project.name,
        clientId: project.clientId,
        clientName: project.clientName,
        items,
        hasVat,
        vatRate: hasVat ? vatRate : 0,
        subtotal,
        vatAmount,
        total,
        status: newStatus || budget?.status || 'draft',
        updatedAt: Timestamp.now(),
      };
      if (newStatus === 'approved') {
        data.approvedAt = Timestamp.now();
        if (!isAdmin) {
          notifyClientBudgetApproved(
            project.clientName || 'Client',
            project.name,
            total
          ).catch(console.error);
        }
      }
      if (newStatus === 'paid') data.paidAt = Timestamp.now();

      if (budget?.id) {
        await updateDoc(doc(db, 'budgets', budget.id), data);
      } else {
        data.createdAt = Timestamp.now();
        await addDoc(collection(db, 'budgets'), data);
      }
      await updateDoc(doc(db, 'projects', project.id), { budget: total, hasVat });
      onClose();
    } catch (err) {
      alert('Error saving budget: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Mark budget as ${STATUS_LABELS[newStatus].label}?`)) return;
    await handleSave(newStatus);
    // Email admin when client approves budget
    if (newStatus === 'approved' && !isAdmin) {
      notifyClientBudgetApproved(
        currentUser?.displayName || currentUser?.email || 'Client',
        project.name,
        total
      ).catch(console.error);
    }
  };

  const status = budget?.status || 'draft';
  const statusCfg = STATUS_LABELS[status];

  if (loading) return (
    <div className="budget-overlay">
      <div className="budget-modal">
        <div className="budget-loading"><Loader className="spinning" size={24} /><p>Loading budget...</p></div>
      </div>
    </div>
  );

  return (
    <div className="budget-overlay" onClick={onClose}>
      <div className="budget-modal" onClick={e => e.stopPropagation()}>

        <div className="budget-header">
          <div className="budget-header-left">
            <div>
              <h2>Budget — {project.name}</h2>
              <div className="budget-header-meta">
                <span className="budget-status-badge" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                  {statusCfg.label}
                </span>
                {project.createdAt && (
                  <span className="budget-project-date">Created {formatDate(project.createdAt)}</span>
                )}
              </div>
            </div>
          </div>
          <button className="budget-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="budget-body">
          {isAdmin && (
            <div className="budget-vat-row">
              <label className="budget-vat-label">
                <input type="checkbox" checked={hasVat} onChange={e => setHasVat(e.target.checked)} disabled={!canEdit} />
                Apply VAT ({vatRate}%)
              </label>
            </div>
          )}

          {groups().map(group => (
            <div key={group.milestoneId} className="budget-group">
              <div className="budget-group-header">
                <div className="budget-group-title-row">
                  <span className="budget-group-title">{group.milestoneName}</span>
                  {group.milestoneDate && (
                    <span className="budget-group-date">{formatDate(group.milestoneDate)}</span>
                  )}
                </div>
                {canEdit && (
                  <button className="btn-add-item" onClick={() => addItem(group.milestoneId, group.milestoneName)}>
                    <Plus size={13} /> Add item
                  </button>
                )}
              </div>

              {group.items.length === 0 ? (
                <p className="budget-group-empty">No items</p>
              ) : (
                <div className="budget-table-wrapper">
                <table className="budget-items-table">
                  <thead>
                    <tr>
                      <th className="col-qty">Qty</th>
                      <th className="col-concept">Concept</th>
                      <th className="col-price">Unit price</th>
                      <th className="col-total">Total</th>
                      {canEdit && <th className="col-actions"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map(item => (
                      <tr key={item.id}>
                        <td className="col-qty">
                          {canEdit ? (
                            <input type="number" value={item.qty} onChange={e => updateItem(item.id, 'qty', e.target.value)} placeholder="1" min="1" step="1" className="budget-input qty-input" />
                          ) : <span>{item.qty || 1}</span>}
                        </td>
                        <td className="col-concept">
                          {canEdit ? (
                            <input type="text" value={item.concept} onChange={e => updateItem(item.id, 'concept', e.target.value)} placeholder="Description..." className="budget-input" />
                          ) : <span>{item.concept}</span>}
                        </td>
                        <td className="col-price">
                          {canEdit ? (
                            <input type="number" value={item.price} onChange={e => updateItem(item.id, 'price', e.target.value)} placeholder="0.00" min="0" step="0.01" className="budget-input price-input" />
                          ) : <span className="price-value">{parseFloat(item.price || 0).toFixed(2)}</span>}
                        </td>
                        <td className="col-total">
                          <span className="price-value">{lineTotal(item).toFixed(2)} €</span>
                        </td>
                        {canEdit && (
                          <td className="col-actions">
                            <button className="btn-remove-item" onClick={() => removeItem(item.id)}><Trash2 size={13} /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          ))}

          <div className="budget-totals">
            <div className="budget-total-row"><span>Subtotal</span><span>{subtotal.toFixed(2)} €</span></div>
            {hasVat && <div className="budget-total-row vat"><span>VAT ({vatRate}%)</span><span>{vatAmount.toFixed(2)} €</span></div>}
            <div className="budget-total-row total"><span>Total</span><span>{total.toFixed(2)} €</span></div>
          </div>
        </div>

        <div className="budget-footer">
          <div className="budget-footer-left">
            {/* Admin actions */}
            {isAdmin && (status === 'draft' || status === 'revision') && (
              <button className="btn-budget-status approve" onClick={() => handleStatusChange('approved')}>
                <CheckCircle size={15} /> Mark Approved
              </button>
            )}
            {isAdmin && status === 'approved' && (
              <>
                <button className="btn-budget-status revision" onClick={() => handleStatusChange('revision')}>
                  Back to Revision
                </button>
                <button className="btn-budget-status paid" onClick={() => handleStatusChange('paid')}>
                  <CreditCard size={15} /> Mark Paid
                </button>
              </>
            )}
            {/* Client actions */}
            {!isAdmin && (status === 'draft' || status === 'revision') && (
              <button className="btn-budget-status approve" onClick={() => handleStatusChange('approved')}>
                <CheckCircle size={15} /> Approve Budget
              </button>
            )}
            {!isAdmin && status === 'approved' && currentUser?.permissions?.canEditBudgets && (
              <button className="btn-budget-status revision" onClick={() => handleStatusChange('revision')}>
                Back to Revision
              </button>
            )}
          </div>
          <div className="budget-footer-right">
            <button className="btn-budget-cancel" onClick={onClose}>Cancel</button>
            {canEdit && (
              <button className="btn-budget-save" onClick={() => handleSave()} disabled={saving}>
                {saving ? <Loader size={15} className="spinning" /> : <Save size={15} />}
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetModal;
